import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import LocalStorageUtil from "../../util/LocalStorageUtil";
import Authentication from "../../util/Authentication";
import { LocalStorageConstants } from "../../constants/ApplicationConstants/LocalStorageConstants";
import { ModuleAndIdMapping, Modules, Operations } from "../../constants/DatabaseConstants/MasterConstant";
import { RouteConstants } from "../../constants/ApplicationConstants/RouteConstants";
import { UserRoleDATA } from "../../constants/DatabaseConstants/AuthConstants";
import { CustomNotification } from "../../util/CustomNotification";
import { ExportFile } from "../../util/ExportFile";
import MasterUtil from "../../util/MasterUtil";
import useValue from "../../hooks/useValue";
import { updateMasterFilters } from "../../appRedux/reducers/Common";
import { allowedCustomerDropdownList, allowedLocationDropdownList, MasterFiltersConstant } from "../../constants/ApplicationConstants/MasterConstant";

import * as masterServices from "../../services/masterServices";
import * as commonAPI from "../../services/commonServices";
import { getTotalCountParkingSlotKeyFobCount } from "../../services/commonServices";

import type { RootState } from "../../appRedux/store";
import type { UserInterface } from "../../shared/interface/auth/User.interface";
import type { KeyFobList } from "../../shared/interface/MasterManagement/keyFobMaster.interface";

const useMasterManagement = () => {
  /* CONSTANT */
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegments = location.pathname?.split("/");
  const pathName = pathSegments[2] as Modules;
  const queryParams = new URLSearchParams(location.search);
  const isVisitor = queryParams.get("isVisitor");

  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  /* STATES */

  /* whether the add button was clicked or not. [BASED ON NEW FRAMEWORK]*/
  const [addRecord, setAddRecord] = useState(false);
  const [bulkEditRecord, setBulkEditRecord] = useState(false);

  /* for addView false is active and true is inactive */
  const [addView, setAddView] = useState<boolean>(false);
  /* searched text state */
  const [searchedText, setSearchedText] = useState<string>("");

  const { masterFilters, customerList, locationList } = useSelector((state: RootState) => state.common);

  const [totalParkingSlots, setTotalParkingSlots] = useState<number | null>(null);
  const [keyFobsCount, setKeyFobsCount] = useState<number | null>(null);
  const [clientList, setClientList] = useState<{ id: number; client_name: string }[]>([]);
  const [keyFobList, setKeyFobList] = useState<KeyFobList[]>([]);

  const { value: excelIconLoading, handleDisableValue: handleDisableExcel, handleEnableValue: handleEnableExcel } = useValue(false);
  const { value: pdfIconLoading, handleDisableValue: handleDisablePdf, handleEnableValue: handleEnablePdf } = useValue(false);
  const { value: dropdownDataLoading, handleDisableValue: handleDisableDropdownDataLoading, handleEnableValue: handleEnableDropdownDataLoading } = useValue(false);

  /* HANDLERS */

  const handleAddClick = () => {
    handleAddRecord(true);
    setAddView(true);

    /* clear search input */
    setSearchedText("");
  };

  const handleAddView = (value: boolean) => {
    setAddView(value);
  };

  const handleBulkEditRecord = (value: boolean) => setBulkEditRecord(value);

  const handleAddRecord = (value: boolean) => {
    setAddRecord(value);
  };

  const handleSearchedText = (value: string) => {
    setSearchedText(value);
  };

  const handleChangeClient = (value: number | null) => {
    updateMasterStateFilters(MasterFiltersConstant.CLIENT_ID, value);
  };

  const handleChangeKeyFob = (value: number | null) => {
    updateMasterStateFilters(MasterFiltersConstant.KEY_FOB_ID, value);
  };

  const updateMasterStateFilters = (key: string, value: number | null | boolean) => {
    const dataObj = {
      [key]: value
    };
    LocalStorageUtil.localStorageUpdateData(LocalStorageConstants.MASTER_FILTERS, dataObj);
    dispatch(updateMasterFilters(dataObj));
  };

  const getMasterDataByModuleId = async (moduleId: number | null) => {
    try {
      if (!moduleId) return;

      const getModuleName = Authentication.getModuleNameById(moduleId);

      const showDeleted = masterFilters?.moduleId === ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER] ? null : true;
      const selectedCustomerId = getModuleName && allowedCustomerDropdownList?.includes(getModuleName) && masterFilters?.customerId ? masterFilters?.customerId : null;
      const selectedLocationId = getModuleName && allowedLocationDropdownList?.includes(getModuleName) && masterFilters?.locationId ? masterFilters?.locationId : null;
      const selectedClientId = selectedCustomerId && selectedLocationId && masterFilters?.clientId ? masterFilters?.clientId : null;
      const selectedKeyFobIdId = selectedCustomerId && selectedLocationId && masterFilters?.keyFobId ? masterFilters?.keyFobId : null;

      const result = await masterServices.getOneMasterListById(masterFilters?.moduleId!, showDeleted, selectedCustomerId, selectedLocationId, selectedClientId, selectedKeyFobIdId);
      return result?.["AllRecordsList"];
    } catch (error) {
      throw error;
    }
  };

  const handleExportExcelFile = async () => {
    try {
      if (!masterFilters?.moduleId) return;
      handleEnableExcel();
      /* get Master Data list */
      const masterData = await getMasterDataByModuleId(masterFilters?.moduleId);
      /* formatted data to show on Excel File only those data get */
      const formattedMasterData = MasterUtil.formattedMasterDataToExportFile(masterData, masterFilters?.moduleId);
      /* get moduleId by name */
      const moduleName = Authentication.getModuleNameById(masterFilters?.moduleId);
      /* formatted module name first letter capital and replace to - to space */
      const formattedModuleName = MasterUtil.formatModuleName(moduleName);
      /* download excel file */
      ExportFile.excelFileDownload(formattedMasterData, formattedModuleName);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableExcel();
    }
  };

  const handleExportPDFFile = async () => {
    try {
      if (!masterFilters?.moduleId) return;
      handleEnablePdf();
      /* get Master Data list */
      const masterData = await getMasterDataByModuleId(masterFilters?.moduleId);
      /* formatted data to show on Excel File only those data get */
      const formattedMasterData = MasterUtil.formattedMasterDataToExportFile(masterData, masterFilters?.moduleId);
      /* get moduleId by name */
      const moduleName = Authentication.getModuleNameById(masterFilters?.moduleId);
      /* formatted module name first letter capital and replace to - to space */
      const formattedModuleName = MasterUtil.formatModuleName(moduleName);

      // Prepare the header information for the PDF
      const customer = customerList?.find((customer) => customer.id === masterFilters?.customerId);
      const location = locationList?.find((location) => location.id === masterFilters?.locationId);
      const keyFob = keyFobList?.find((keyFob) => keyFob.id === masterFilters?.keyFobId);
      const client = clientList?.find((client) => client.id === masterFilters?.clientId);

      const reportName = formattedModuleName;

      const headerInfo: any = {
        header: `${userData?.config?.theme?.HEADER_TEXT || ""}`,
        customerName: `${customer?.customer_name || ""}`,
        locationName: `${location?.location_name || ""}`,
        reportName,
        clientName: `${client?.client_name || ""}`,
        keyFobToken: `${keyFob?.key_fob_token || ""}`
      };

      /* download pdf file */
      ExportFile.pdfFileDownload(formattedMasterData, formattedModuleName, headerInfo);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisablePdf();
    }
  };

  /* select id to get list of master */
  const handleListSelect = (value: number) => {
    /* when add is selected, we need to disable this button.  */
    setAddView(false);
    setAddRecord(false);
    updateMasterStateFilters(MasterFiltersConstant.MODULE_ID, value);

    /* clear search input */
    setSearchedText("");

    const moduleName = Authentication.getModuleNameById(value);
    navigate(`${RouteConstants.MASTER_MANAGEMENT_PAGE}/${moduleName}`);
  };

  const handleChangeOnSearch = (value: string) => {
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
  };

  const handleChangeSearch = (value: string) => {
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
  };

  /* customer and location selected important to add button active */
  const isAddButtonDisabled = (): boolean => {
    const { customerId, locationId, moduleId } = masterFilters || {};

    const locationRequiredModules = [ModuleAndIdMapping[Modules.KEY_FOB_MASTER], ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER], ModuleAndIdMapping[Modules.CUSTOMER_CLIENT_MASTER], ModuleAndIdMapping[Modules.PRICING_MASTER]];

    /* Condition 1: locationId is required for certain modules */ 
    if (!locationId && locationRequiredModules.includes(moduleId as number)) {
      return true;
    }

    /* Condition 2: customerId is required for CUSTOMER_LOCATION_MASTER */ 
    if (!customerId && moduleId === ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]) {
      return true;
    }

    return false;
  };

  /* EFFECT */
  /* update pathname to selectedId state */
  useEffect(() => {
    if (!pathName) return;
    const getModuleId = ModuleAndIdMapping[pathName];

    if (getModuleId !== ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER]) {
      updateMasterStateFilters(MasterFiltersConstant.CLIENT_ID, null);
      updateMasterStateFilters(MasterFiltersConstant.KEY_FOB_ID, null);
    }

    updateMasterStateFilters(MasterFiltersConstant.MODULE_ID, getModuleId);
    updateMasterStateFilters(MasterFiltersConstant.REFRESH, true);

    if (pathName && Authentication.hasModuleAndOperationAccess(userData, pathName, Operations.READ)) {
      navigate(`${RouteConstants.MASTER_MANAGEMENT_PAGE}/${pathName}?${queryParams.toString()}`);
    } else if (pathName === Modules.WEB_FE_ACCESS_CONTROL_MASTER && userData?.role === UserRoleDATA.PARI_ADMIN) {
      navigate(`${RouteConstants.MASTER_MANAGEMENT_PAGE}/${pathName}`);
    } else {
      navigate(`${RouteConstants.MASTER_MANAGEMENT_PAGE}/default`);
    }
  }, [pathName]);

  /* Dashboard Redirect if isVisitor true then open add modal  */
  useEffect(() => {
    if (isVisitor) handleAddClick();
  }, [isVisitor]);

  /* Fetch parking slots and key fobs count when the selectedId is for KEY_FOB_MASTER */
  useEffect(() => {
    if (masterFilters?.moduleId === ModuleAndIdMapping[Modules.KEY_FOB_MASTER] && masterFilters?.customerId && masterFilters?.locationId) {
      getTotalCountParkingSlotKeyFobCount(masterFilters?.customerId, masterFilters?.locationId)
        .then((response) => {
          setTotalParkingSlots(response.data.totalParkingSlots);
          setKeyFobsCount(response.data.keyFobsCount);
        })
        .catch((error) => {
          CustomNotification.showErrorMessage(error);
        });
    }
  }, [masterFilters?.moduleId, masterFilters?.customerId, masterFilters?.locationId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        handleEnableDropdownDataLoading();
        if (!masterFilters?.customerId || !masterFilters?.locationId) {
          setClientList([]);
          setKeyFobList([]);
          return;
        }

        const [clientListResponse, keyFobListResponse] = await Promise.all([commonAPI.getClientListByCustomerAndLocationId(masterFilters?.customerId, masterFilters?.locationId), commonAPI.getKeyFobListByCustomerAndLocationId(masterFilters?.customerId, masterFilters?.locationId)]);

        setClientList(clientListResponse?.data || []);
        setKeyFobList(keyFobListResponse?.data || []);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        handleDisableDropdownDataLoading();
      }
    };

    if (masterFilters?.moduleId === ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER]) fetchData();
  }, [masterFilters?.moduleId, masterFilters?.customerId, masterFilters?.locationId]);

  return {
    isAddButtonDisabled,
    addView,
    addRecord,
    handleListSelect,
    handleSearchedText,
    handleAddRecord,
    handleAddView,
    searchedText,
    userData,
    handleAddClick,
    selectedId: masterFilters?.moduleId,
    handleChangeOnSearch,
    handleChangeSearch,
    handleBulkEditRecord,
    bulkEditRecord,
    handleExportExcelFile,
    handleExportPDFFile,
    excelIconLoading,
    pdfIconLoading,
    totalParkingSlots,
    keyFobsCount,
    clientList,
    keyFobList,
    handleChangeClient,
    handleChangeKeyFob,
    masterFilters,
    dropdownDataLoading,
    handleDisableDropdownDataLoading,
    handleEnableDropdownDataLoading
  };
};

export default useMasterManagement;
